import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Avatar,
  Stack,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  alpha,
  Divider,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  Edit,
  LinkedIn,
  Twitter,
  Instagram,
  Facebook,
  WhatsApp,
  EmojiEvents,
  TrendingUp,
  TrackChanges,
  MailOutline,
  School,
  VerifiedUser,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Header from "../Components/Header";
import StudentSidebar from "./StudentSdebar";
import EditProfileModal from "./EditProfileModal";
import defaultPic from "../Images/default.jpeg";

const GOLD = "#D4AF37";
const API_BASE_URL = "https://tdtlworld.com/dss-kaas-backend/";

export default function Profile() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [user, setUser] = useState({
    name: "",
    email: "",
    bio: "",
    tagline: "",
    profile_pic: null,
  });

  const isDarkMode = theme.palette.mode === "dark";
  const token = localStorage.getItem("jwttoken");

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API_BASE_URL}lms/user/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser({
        name: `${resp.data.first_name} ${resp.data.last_name}`,
        email: resp.data.email,
        bio: resp.data.bio,
        tagline: resp.data.tagline,
        profile_pic: resp.data.profile_pic,
      });
    } catch (err) {
      if (err.response?.status === 401) navigate("/Log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, [token]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", bgcolor: "background.default" }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", bgcolor: "background.default" }}>
      <Header />
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <StudentSidebar onToggle={setSidebarExpanded} />
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: { xs: 2, md: 4, lg: 6 },
            width: sidebarExpanded ? "calc(100% - 260px)" : "calc(100% - 80px)",
            transition: theme.transitions.create("width", { duration: 300 }),
            height: "calc(100vh - 64px)",
            overflowY: "auto",
          }}
        >
          <Container maxWidth="lg">
            {/* Profile Header Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Paper 
                elevation={0}
                sx={{ 
                  p: { xs: 3, md: 6 }, 
                  borderRadius: "32px", 
                  border: "1px solid", 
                  borderColor: "divider",
                  position: "relative",
                  overflow: "hidden",
                  mb: 4,
                  bgcolor: "background.paper",
                  boxShadow: isDarkMode ? "0 4px 20px rgba(0,0,0,0.5)" : "0 4px 20px rgba(0,0,0,0.05)"
                }}
              >
                {/* Background Accent */}
                <Box sx={{ 
                  position: "absolute", 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  height: 120, 
                  background: isDarkMode 
                    ? `linear-gradient(180deg, ${alpha(GOLD, 0.15)} 0%, transparent 100%)`
                    : `linear-gradient(180deg, ${alpha(GOLD, 0.08)} 0%, transparent 100%)`,
                  borderBottom: "1px solid",
                  borderColor: alpha(GOLD, 0.1)
                }} />
  
                <Stack direction={{ xs: "column", md: "row" }} spacing={4} alignItems={{ xs: "center", md: "flex-start" }} sx={{ position: "relative", zIndex: 1 }}>
                  <Avatar 
                    src={user.profile_pic || defaultPic} 
                    sx={{ 
                      width: 180, 
                      height: 180, 
                      border: `8px solid ${theme.palette.background.paper}`, 
                      boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                      background: GOLD
                    }} 
                  />
                  
                  <Box sx={{ flexGrow: 1, textAlign: { xs: "center", md: "left" }, pt: { md: 2 } }}>
                    <Stack direction="row" alignItems="center" justifyContent={{ xs: "center", md: "flex-start" }} spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: "-1px", color: "text.primary" }}>{user.name}</Typography>
                      <VerifiedUser sx={{ color: GOLD, fontSize: 32 }} />
                    </Stack>
                    <Typography variant="h6" color={GOLD} fontWeight={700} sx={{ mb: 1.5, opacity: 0.9 }}>{user.tagline || "Enthusiastic Learner"}</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mb: 3, lineHeight: 1.6 }}>
                      {user.bio || "No bio provided yet. Add a few words about yourself to inspire other learners!"}
                    </Typography>
                    
                    <Stack direction="row" spacing={2} justifyContent={{ xs: "center", md: "flex-start" }} sx={{ mb: 4 }}>
                      {[
                        { icon: <Instagram />, color: "#E1306C", label: "Instagram" },
                        { icon: <Twitter />, color: "#1DA1F2", label: "Twitter" },
                        { icon: <Facebook />, color: "#4267B2", label: "Facebook" },
                        { icon: <WhatsApp />, color: "#25D366", label: "WhatsApp" }
                      ].map((social, i) => (
                        <Tooltip key={i} title={social.label}>
                          <IconButton 
                            sx={{ 
                              color: social.color,
                              bgcolor: alpha(social.color, 0.1),
                              "&:hover": { bgcolor: alpha(social.color, 0.2), transform: "translateY(-3px)" },
                              transition: "0.2s"
                            }}
                          >
                            {social.icon}
                          </IconButton>
                        </Tooltip>
                      ))}
                    </Stack>
  
                    <Button 
                      variant="contained" 
                      startIcon={<Edit />}
                      onClick={() => setShowEditModal(true)}
                      sx={{ 
                        borderRadius: "50px", 
                        px: 4, 
                        py: 1.2,
                        bgcolor: GOLD, 
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: "0.95rem",
                        boxShadow: `0 8px 20px ${alpha(GOLD, 0.4)}`,
                        "&:hover": { 
                          bgcolor: alpha(GOLD, 0.9),
                          boxShadow: `0 10px 25px ${alpha(GOLD, 0.5)}`,
                          transform: "scale(1.02)"
                        },
                        transition: "0.2s"
                      }}
                    >
                      Edit Profile
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </motion.div>
  
            {/* Achievement / Stats Section */}
            <Grid container spacing={3}>
              {[
                { title: "Top Performer", value: "Level 12", icon: <EmojiEvents />, color: GOLD, desc: "Top 5% in Web Dev", delay: 0.1 },
                { title: "Current GPA", value: "3.8 / 4.0", icon: <TrendingUp />, color: "#4169E1", desc: "Consistently improving", delay: 0.2 },
                { title: "Learning Goal", value: "80% Complete", icon: <TrackChanges />, color: "#32CD32", desc: "Next milestone: Certification", delay: 0.3 },
              ].map((stat, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: stat.delay, duration: 0.4 }}
                  >
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 3, 
                        borderRadius: "24px", 
                        height: "100%",
                        bgcolor: "background.paper",
                        border: "1px solid", 
                        borderColor: alpha(stat.color, 0.2),
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        overflow: "hidden",
                        "&:hover": { 
                          transform: "translateY(-8px)", 
                          borderColor: stat.color,
                          boxShadow: `0 12px 30px ${alpha(stat.color, 0.15)}`,
                          "& .stat-icon-bg": { transform: "scale(1.1) rotate(5deg)" }
                        }
                      }}
                    >
                      <Box sx={{ position: "absolute", top: -20, right: -20, opacity: 0.05, transform: "rotate(-15deg)" }}>
                        {React.cloneElement(stat.icon, { sx: { fontSize: 120, color: stat.color } })}
                      </Box>
                      
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, position: "relative" }}>
                        <Box 
                          className="stat-icon-bg"
                          sx={{ 
                            p: 1.5, 
                            borderRadius: "14px", 
                            bgcolor: alpha(stat.color, isDarkMode ? 0.2 : 0.1), 
                            color: stat.color,
                            transition: "0.3s ease"
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Typography variant="h6" fontWeight={800} sx={{ color: "text.primary" }}>{stat.title}</Typography>
                      </Stack>
                      
                      <Typography variant="h4" fontWeight={900} color={stat.color} sx={{ mb: 1, letterSpacing: "-0.5px" }}>{stat.value}</Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ opacity: 0.8 }}>{stat.desc}</Typography>
                      
                      <Box 
                        sx={{ 
                          mt: 3, 
                          height: 6, 
                          width: "100%", 
                          bgcolor: alpha(stat.color, 0.1), 
                          borderRadius: 3,
                          overflow: "hidden"
                        }}
                      >
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: "100%" }}
                          transition={{ delay: 0.5 + stat.delay, duration: 1 }}
                          style={{ height: "100%", background: stat.color, borderRadius: 3 }}
                        />
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
  
            {/* Account Details / Security Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  mt: 4, 
                  borderRadius: "24px",
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: isDarkMode ? "0 4px 15px rgba(0,0,0,0.3)" : "0 4px 15px rgba(0,0,0,0.03)"
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                  <VerifiedUser sx={{ color: GOLD }} />
                  <Typography variant="h6" fontWeight={800} sx={{ color: "text.primary" }}>Account Security</Typography>
                </Stack>
                
                <Divider sx={{ mb: 3, opacity: 0.6 }} />
                
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ letterSpacing: "1px", textTransform: "uppercase" }}>Primary Email</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MailOutline sx={{ color: "text.secondary", fontSize: 20 }} />
                        <Typography variant="body1" fontWeight={700} sx={{ color: "text.primary" }}>{user.email || "loading..."}</Typography>
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="flex-end" spacing={2}>
                      <Button 
                        variant="outlined"
                        sx={{ 
                          color: GOLD, 
                          borderColor: GOLD, 
                          borderRadius: "12px",
                          fontWeight: 700,
                          px: 3,
                          "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.05) }
                        }}
                      >
                        Change Password
                      </Button>
                      <Button 
                        variant="text"
                        sx={{ 
                          color: "#d32f2f", 
                          fontWeight: 700,
                          "&:hover": { bgcolor: alpha("#d32f2f", 0.05) }
                        }}
                      >
                        Delete Account
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </motion.div>
          </Container>
        </Box>
      </Box>
  
      {showEditModal && (
        <EditProfileModal 
          show={showEditModal} 
          setShow={setShowEditModal} 
          onEditProfile={fetchUserProfile}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </Box>
  );
}

